import type {
  ApiResponse,
  ApiResponseEnvelope,
  PaginatedResponse,
} from '@/shared/api/types/api-response'
import type {
  CreateTicketConfigPayload,
  ITicketConfig,
  ITicketConfigDetail,
  TicketConfigListParams,
  TicketConfigListResponse,
  UpdateTicketConfigPayload,
} from '@/shared/api/types/ticket-config-types'
import { GW } from '@/shared/api/endpoints'
import { buildSkipLanguageHeaderConfig } from '@/shared/api/utils/skip-language-header'
import { buildTenantHeaderConfig } from '@/shared/api/utils/tenant-header'
import { unwrapApiEnvelope } from '@/shared/api/utils/unwrapapi'
import { request } from '@/shared/libs/request'

const TICKET_CONFIGS_PATH = 'ticket-configs'

export async function getTicketConfigs(
  params: TicketConfigListParams,
  tenantId?: string,
): Promise<TicketConfigListResponse> {
  const response = await request.get<ApiResponseEnvelope<PaginatedResponse<ITicketConfig>>>(
    GW.packaging(TICKET_CONFIGS_PATH),
    {
      ...buildTenantHeaderConfig(tenantId),
      params: { current: params.current, pageSize: params.pageSize },
    },
  )
  const data = unwrapApiEnvelope(response.data)
  return {
    data: {
      data: data.data,
      total: data.total,
      current: data.current,
      pageSize: data.pageSize,
    },
  }
}

export async function getTicketConfigById(
  id: string,
  tenantId?: string,
): Promise<ApiResponse<ITicketConfigDetail>> {
  const response = await request.get<ApiResponseEnvelope<ITicketConfigDetail>>(
    GW.packaging(`${TICKET_CONFIGS_PATH}/${id}`),
    { ...buildTenantHeaderConfig(tenantId), ...buildSkipLanguageHeaderConfig() },
  )
  return { data: unwrapApiEnvelope(response.data) }
}

export async function createTicketConfig(
  payload: CreateTicketConfigPayload,
  tenantId?: string,
): Promise<ApiResponse<ITicketConfig>> {
  const response = await request.post<ApiResponseEnvelope<ITicketConfig>>(
    GW.packaging(TICKET_CONFIGS_PATH),
    payload,
    buildTenantHeaderConfig(tenantId),
  )
  return { data: unwrapApiEnvelope(response.data), message: 'Ticket config created successfully' }
}

export async function updateTicketConfig(
  id: string,
  payload: UpdateTicketConfigPayload,
  tenantId?: string,
): Promise<ApiResponse<ITicketConfig>> {
  const response = await request.patch<ApiResponseEnvelope<ITicketConfig>>(
    GW.packaging(`${TICKET_CONFIGS_PATH}/${id}`),
    payload,
    buildTenantHeaderConfig(tenantId),
  )
  return { data: unwrapApiEnvelope(response.data), message: 'Ticket config updated successfully' }
}

export async function toggleTicketConfigActive(
  id: string,
  isActive: boolean,
  tenantId?: string,
): Promise<ApiResponse<ITicketConfig>> {
  const response = await request.put<ApiResponseEnvelope<ITicketConfig>>(
    GW.packaging(`${TICKET_CONFIGS_PATH}/${id}/active`),
    { isActive },
    buildTenantHeaderConfig(tenantId),
  )
  return { data: unwrapApiEnvelope(response.data), message: 'Ticket config status updated' }
}
